import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AiOnboardingSubmissionOutcome,
  setAiOnboardingCallActive,
} from '@pharmaplan/common';

import { useAppDispatch } from '../useAppDispatch';
import strings from '../../localization';
import OutcomeModal from '../../components/Modals/OutcomeModal';
import { Constants, OutcomeModalTypes } from '../../helpers/Constants';
import { resetDialog, setDialog } from '../../reducers/dialogReducer';

// The agent creates the profile at the end of the call and says the closing
// message before ending it. The login redirect waits for that end: a created
// profile uses the regular signup success redirect, which the active-call flag
// holds back until then, and a failed one shows an error on the login screen.
const useAiOnboardingCompletion = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const submissionOutcomeRef = useRef<AiOnboardingSubmissionOutcome | null>(
    null,
  );

  const handleProfileSubmissionSettled = useCallback(
    (outcome: AiOnboardingSubmissionOutcome) => {
      submissionOutcomeRef.current = outcome;
    },
    [],
  );

  const showSubmissionFailure = useCallback(() => {
    navigate(Constants.paths.login);
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <OutcomeModal
            customClose={() =>
              dispatch(resetDialog())}
            message={strings.aiOnboardingSubmissionFailedMessage}
            type={OutcomeModalTypes.error}
          />
        ),
        showCloseButton: true,
      }),
    );
  }, [dispatch, navigate]);

  // Returns whether the ended call reached profile submission, in which case
  // the outcome decides where the professional goes next.
  const handleConversationEnded = useCallback(() => {
    const outcome = submissionOutcomeRef.current;

    if (outcome === null) {
      return false;
    }

    dispatch(setAiOnboardingCallActive(false));

    if (outcome === AiOnboardingSubmissionOutcome.Failed) {
      showSubmissionFailure();
    }

    return true;
  }, [dispatch, showSubmissionFailure]);

  useEffect(() => {
    dispatch(setAiOnboardingCallActive(true));

    return () => {
      dispatch(setAiOnboardingCallActive(false));
    };
  }, [dispatch]);

  return {
    handleConversationEnded,
    handleProfileSubmissionSettled,
  };
};

export default useAiOnboardingCompletion;
